import { maskMoney, maskZipCode } from '../../../services/masks';
import { getActivationValue } from './activationFormHelpers';

export const bankAccountTypeLabel: Record<string, string> = {
  checking: 'Conta corrente',
  savings: 'Poupança'
};

export const formatDateTime = (value?: string | Date | null): string => {
  if (value === undefined || value === null || value === '') return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR');
};

export const formatBirthdate = (value: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [yyyy, mm, dd] = value.split('-');
    return `${dd}/${mm}/${yyyy}`;
  }
  return value || '-';
};

export const formatMonthlyIncome = (value: string): string => {
  if (!value) return '-';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return `R$ ${maskMoney(String(numeric.toFixed(2)), true)}`;
};

export const formatBankAccount = (bankAccount: Record<string, unknown>): string => {
  const bank = getActivationValue(bankAccount, 'bank');
  const branchNumber = getActivationValue(bankAccount, 'branch_number');
  const branchCheckDigit = getActivationValue(bankAccount, 'branch_check_digit');
  const accountNumber = getActivationValue(bankAccount, 'account_number');
  const accountCheckDigit = getActivationValue(bankAccount, 'account_check_digit');
  const accountType = getActivationValue(bankAccount, 'type');

  const branch = branchCheckDigit ? `${branchNumber}-${branchCheckDigit}` : branchNumber;
  const account = accountCheckDigit ? `${accountNumber}-${accountCheckDigit}` : accountNumber;

  return [
    bank && `Banco ${bank}`,
    branch && `Ag. ${branch}`,
    account && `Conta ${account}`,
    (accountType && bankAccountTypeLabel[accountType]) || accountType
  ]
    .filter(Boolean)
    .join(' · ') || '-';
};

export type ActivationDisplaySource = {
  recipientType?: string;
  registerInformation?: Record<string, unknown>;
  defaultBankAccount?: Record<string, unknown>;
  payload?: Record<string, unknown>;
  pagarmeRecipientId?: string;
  lastSyncAt?: string | Date | null;
  lastSyncStatus?: string;
  lastSyncError?: string;
  completedAt?: string | Date | null;
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
};

export const pickRegisterAndBankFromActivation = (source?: ActivationDisplaySource | null) => {
  const activation = source || {};
  const payload = (activation.payload || {}) as Record<string, unknown>;
  const registerInformation = (activation.registerInformation ||
    payload.registerInformation ||
    payload.register_information ||
    {}) as Record<string, unknown>;
  const defaultBankAccount = (activation.defaultBankAccount ||
    payload.defaultBankAccount ||
    payload.default_bank_account ||
    {}) as Record<string, unknown>;
  const recipientType =
    activation.recipientType ||
    (registerInformation.type as string | undefined) ||
    'individual';
  const isCorporation = recipientType === 'corporation';
  const address = (isCorporation
    ? registerInformation.main_address
    : registerInformation.address) as Record<string, unknown>;
  const partner = (
    Array.isArray(registerInformation.managing_partners)
      ? registerInformation.managing_partners[0]
      : null
  ) as Record<string, unknown> | null;

  return {
    recipientType,
    isCorporation,
    registerInformation,
    defaultBankAccount,
    address,
    partner,
    pagarmeRecipientId: activation.pagarmeRecipientId || '',
    lastSyncAt: activation.lastSyncAt,
    lastSyncStatus: activation.lastSyncStatus || '',
    lastSyncError: activation.lastSyncError || '',
    completedAt: activation.completedAt,
    createdAt: activation.createdAt,
    updatedAt: activation.updatedAt
  };
};

export const formatZipCodeDisplay = (address?: Record<string, unknown>): string => {
  const zipCode = getActivationValue(address || {}, 'zip_code');
  return zipCode ? maskZipCode(zipCode.replace(/\D/g, '')) : '-';
};

export const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
});
